import { useEffect, useRef, useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { usePostHogTracking, PostHogEvents } from '../utils/posthogEvents';

/**
 * Custom hook for learning platform analytics
 * Provides easy-to-use tracking functions for common user interactions
 */
export const usePostHogAnalytics = () => {
  const posthog = usePostHog();
  const tracking = usePostHogTracking(posthog);

  return tracking;
};

/**
 * Hook for tracking typing behavior in chat interfaces
 */
export const useTypingAnalytics = () => {
  const { trackTypingSession } = usePostHogAnalytics();
  const typingStartRef = useRef<number | null>(null);
  const characterCountRef = useRef<number>(0);

  const startTyping = useCallback(() => {
    if (typingStartRef.current === null) {
      typingStartRef.current = Date.now();
      trackTypingSession(true);
    }
  }, [trackTypingSession]);

  const endTyping = useCallback(() => {
    if (typingStartRef.current !== null) {
      const duration = Date.now() - typingStartRef.current;
      trackTypingSession(false, {
        duration_ms: duration,
        characters_typed: characterCountRef.current
      });
      typingStartRef.current = null;
      characterCountRef.current = 0;
    }
  }, [trackTypingSession]);

  const updateCharacterCount = useCallback((count: number) => {
    characterCountRef.current = count;
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (typingStartRef.current !== null) {
        endTyping();
      }
    };
  }, [endTyping]);

  return {
    startTyping,
    endTyping,
    updateCharacterCount
  };
};

/**
 * Hook for tracking message reading time using Intersection Observer
 */
export const useMessageReadingAnalytics = (messageId: string, messageContent: string) => {
  const { trackMessageReading } = usePostHogAnalytics();
  const startTimeRef = useRef<number | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const createObserver = useCallback((element: HTMLElement) => {
    if (!element || observerRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Message came into view
            startTimeRef.current = Date.now();
          } else if (startTimeRef.current !== null) {
            // Message left view
            const readingTime = Date.now() - startTimeRef.current;
            if (readingTime > 500) { // Only track if read for more than 500ms
              trackMessageReading(messageId, readingTime, messageContent.length);
            }
            startTimeRef.current = null;
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of the message is visible
        rootMargin: '0px'
      }
    );

    observerRef.current.observe(element);
  }, [messageId, messageContent, trackMessageReading]);

  const cleanup = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    startTimeRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    observeElement: createObserver,
    cleanup
  };
};

/**
 * Hook for tracking page focus/blur events
 */
export const usePageFocusAnalytics = (pageName: string) => {
  const { trackEngagement } = usePostHogAnalytics();
  const focusStartRef = useRef<number | null>(null);

  useEffect(() => {
    const handleFocus = () => {
      focusStartRef.current = Date.now();
      trackEngagement(PostHogEvents.PAGE_FOCUSED, {
        feature_area: 'page_attention',
        page_category: pageName.toLowerCase().replace(/\s+/g, '_')
      });
    };

    const handleBlur = () => {
      if (focusStartRef.current !== null) {
        const focusTime = Date.now() - focusStartRef.current;
        trackEngagement(PostHogEvents.PAGE_BLURRED, {
          time_spent_ms: focusTime,
          feature_area: 'page_attention',
          page_category: pageName.toLowerCase().replace(/\s+/g, '_')
        });
        focusStartRef.current = null;
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Track initial focus if page is already focused
    if (document.hasFocus()) {
      handleFocus();
    }

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      if (focusStartRef.current !== null) {
        handleBlur();
      }
    };
  }, [pageName, trackEngagement]);
};

/**
 * Hook for tracking document upload events
 */
export const useDocumentUploadAnalytics = () => {
  const { trackLearning } = usePostHogAnalytics();

  const trackUploadStart = useCallback((fileName: string, fileSize: number, fileType: string, uploadMethod: string = 'unknown') => {
    trackLearning(PostHogEvents.DOCUMENT_UPLOAD_STARTED, {
      file_type: fileType,
      file_size_mb: Math.round((fileSize / 1024 / 1024) * 100) / 100,
      document_category: getDocumentCategory(fileType),
      upload_method: uploadMethod
    });
  }, [trackLearning]);

  const trackUploadComplete = useCallback((fileName: string, processingTime: number, documentId?: string) => {
    trackLearning(PostHogEvents.DOCUMENT_UPLOADED, {
      document_id: documentId,
      processing_time_ms: processingTime,
      upload_success: true
    });
  }, [trackLearning]);

  const trackUploadError = useCallback((fileName: string, errorMessage: string) => {
    trackLearning(PostHogEvents.DOCUMENT_UPLOAD_FAILED, {
      error_message: errorMessage,
      upload_success: false
    });
  }, [trackLearning]);

  return {
    trackUploadStart,
    trackUploadComplete,
    trackUploadError
  };
};

/**
 * Helper function to categorize documents by file type
 */
function getDocumentCategory(fileType: string): string {
  if (fileType.includes('pdf')) return 'pdf_document';
  if (fileType.includes('image')) return 'image_file';
  if (fileType.includes('text')) return 'text_document';
  if (fileType.includes('word') || fileType.includes('doc')) return 'word_document';
  if (fileType.includes('presentation') || fileType.includes('ppt')) return 'presentation';
  return 'other_document';
}